"use client";

import { useMemo } from "react";
import type { CSSProperties } from "react";
import { resolve_icon_layout_size_px } from "./icon-size-tokens";
import { resolve_icon_stroke_width } from "./icon-stroke-config";
import type { IconProps } from "./icon-props";

// MARK: useIconLayout
/**
 * Resolves {@link IconProps} into SVG layout: rem dimensions, stroke width, merged `style` (color CSS var + caller `style`).
 * Uses `useMemo` for the merged style object so referentially stable parents avoid unnecessary child updates when only unrelated props change.
 *
 * **Reuse scope:** Project-wide — hand-authored icon components (`plus.tsx`, etc.).
 */
export function useIconLayout(props: IconProps) {
    const {
        color ,
        colorIntensity ,
        size: size_prop,
        strokeWidth,
        style,
        className,
        width,
        height,
        ...rest
    } = props;

    const resolved_size = resolve_icon_layout_size_px(size_prop);
    const width_rem = `${resolved_size / 16}rem`;
    const height_rem = `${resolved_size / 16}rem`;
    const resolved_width = width ?? width_rem;
    const resolved_height = height ?? height_rem;
    const effective_stroke_width = resolve_icon_stroke_width(resolved_size, strokeWidth);

    const svg_style = useMemo(() => {
        const default_style: CSSProperties = {
            color: !color && !colorIntensity ? `var(--foreground)` : `var(--${String(color || "neutral")}-${colorIntensity || 500})`,
        };
        return { ...default_style, ...style };
    }, [color, colorIntensity, style]);

    return {
        resolved_width,
        resolved_height,
        effective_stroke_width,
        className,
        svg_style,
        rest,
    };
}
